/* eslint-disable import/no-unresolved */
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Grid,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AdminShopHeader from "components/pages/admin/shops/AdminShopHeader";
import * as Actions from "actions";
import ButtonComponent from "../../../../Button";
import {getLocalDate, getPortOrSquareImage} from "../../../../../helpers/utils";
import {DATE_TIME_FORMAT_SHORT} from "../../../../../config/constants";
import AdminShopSalesLogList from "components/elements/admin/shops/AdminShopSalesLogList";
const LIMIT = 20;

const SOURCE_BY_KIND = {
  user: "virtual_tryon",
  model: "seller_model_tryon,seller_bulk_model_tryon",
};

function tryOnPreviewUrl(row) {
  const firstResult = row ;
  if (firstResult && firstResult.result_images) {
    return getPortOrSquareImage(firstResult.result_images);
  }
  const g = row.garment_image;
  if (g && g.cdn) {
    return getPortOrSquareImage([g]);
  }
  return null;
}

function userLabel(row) {
  const u = row.user_id;
  if (!u) return "—";
  const name = [u.firstname, u.lastname].filter(Boolean).join(" ").trim();
  return name || u.email || u.mobile || "—";
}

const AdminShopVirtualTryOns = (props) => {
  const { match, onCancel } = props;
  const dispatch = useDispatch();
  const adminVirtualTryOns = useSelector((state) => state.adminVirtualTryOns);

  const shopId = match?.params?.shopId || props.shopId;

  const [kind, setKind] = useState("model");
  const [page, setPage] = useState(1);

  const rows = adminVirtualTryOns?.page?.tryons || [];
  const total = adminVirtualTryOns?.page?.total || 0;
  const loading = !!adminVirtualTryOns?.isLoading;
  const error =
    adminVirtualTryOns?.serviceError?.statusMessage ||
    adminVirtualTryOns?.serviceError?.message ||
    "";

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(total / LIMIT)),
    [total],
  );

  useEffect(() => {
    if (!shopId) return undefined;

    const skip = (page - 1) * LIMIT;
    dispatch(
      Actions.adminGetShopVirtualTryOns(shopId, {
        limit: LIMIT,
        skip,
        source: SOURCE_BY_KIND[kind] || SOURCE_BY_KIND.user,
      }),
    );
    return undefined;
  }, [dispatch, shopId, page, kind]);

  useEffect(() => {
    if (!shopId) return undefined;
    return () => {
      dispatch(Actions.adminResetShopVirtualTryOns());
    };
  }, [dispatch, shopId]);

  const onKindChange = (_e, next) => {
    if (next == null) return;
    setKind(next);
    setPage(1);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box mb={2}>
        <ButtonComponent
            type="primary"
            variant="outlined"
            name="< Back"
            size="small"
            onClick={onCancel}
        />
      </Box>
      <Box sx={{ mt: 2, mb: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
        <Typography variant="h6" component="h2">
          Virtual try-ons
        </Typography>
        <ToggleButtonGroup
          value={kind}
          exclusive
          onChange={onKindChange}
          size="small"
          color="primary"
        >
          <ToggleButton value="model">Model try-ons</ToggleButton>
          <ToggleButton value="user">User try-ons</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">
          {total} total
        </Typography>
      </Box>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}
      {!loading && rows.length === 0 && !error && (
        <Typography color="text.secondary">No records for this filter.</Typography>
      )}
      <Grid container spacing={2}>
        {rows.map((row) => {
          const img = tryOnPreviewUrl(row);
          const productName =
            (row.product_id && row.product_id.name) || "Product";
          return (
            <Grid item xs={12} sm={6} md={4} key={row._id}>
              <Card variant="outlined">
                {img ? (
                  <CardMedia
                    component="img"
                    height="200"
                    image={img}
                    alt=""
                    sx={{ objectFit: "contain", bgcolor: "grey.100" }}
                  />
                ) : (
                  <Box
                    height={200}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor="grey.100"
                  >
                    <Typography variant="caption" color="text.secondary">
                      No preview
                    </Typography>
                  </Box>
                )}
                <CardContent>
                  <Typography variant="subtitle1" noWrap title={productName}>
                    {productName}
                  </Typography>
                  <Typography variant="subtitle1" noWrap title={productName}>
                    {getLocalDate(row.created_at, DATE_TIME_FORMAT_SHORT)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {userLabel(row)}
                  </Typography>
                  <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    <Chip size="small" label={row.status || "—"} />
                    <Chip size="small" variant="outlined" label={row.source || "—"} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      {!loading && total > LIMIT && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_e, p) => setPage(p)}
            color="primary"
          />
        </Box>
      )}
      <AdminShopSalesLogList shopId={shopId} />
    </Box>
  );
};

AdminShopVirtualTryOns.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      shopId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  shopId: PropTypes.string,
};

export default AdminShopVirtualTryOns;
